/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  FieldName,
  FieldValues,
  SetFieldValue,
  SetValueConfig,
} from "react-hook-form";
import { Text } from "@nordcloud/gnui";
import { DatePickerField } from "~/components/DatePicker/DatePickerField";
import { EditButton } from "~/components/InlineEdit";
import FlexContainer from "~/components/Styled/FlexContainer";
import { InlineActionButton } from "~/components/Styled/InlineActionButton";
import { dateFormat } from "~/constants";
import { Register } from "~/models";

type SetDates = React.Dispatch<
  React.SetStateAction<{
    softLaunch: string;
    goLive: string;
    offboarding: string;
  }>
>;

type Dates = {
  softLaunch: string;
  goLive: string;
  offboarding: string;
};

type DatePickerProps = {
  register: Register;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dates: any;
  name: string;
  setDates: SetDates;
  setValue: (
    name: FieldName<FieldValues>,
    value: SetFieldValue<FieldValues>,
    config?: SetValueConfig
  ) => void;
  canEdit?: boolean;
  mandatoryDate?: boolean;
};

function DatePicker({
  register,
  dates,
  name,
  setDates,
  setValue,
  canEdit = true,
  mandatoryDate = false,
}: DatePickerProps) {
  dayjs.extend(utc);

  const [editMode, setEditMode] = React.useState(false);

  const clearDate = () => {
    setValue(name, null);
    setDates((prevState: Dates) => ({
      ...prevState,
      [name]: null,
    }));
  };

  React.useEffect(() => {
    dates?.[name] && setValue(name, dates?.[name]);
  }, [dates, name, setValue]);

  React.useEffect(() => {
    register({ name: name, value: dates?.[name] });
  }, [dates, name, register]);

  const updateValue = (date: string) => {
    setDates((prevState: Dates) => ({
      ...prevState,
      [name]: date,
    }));
  };

  const value = dates?.[name];

  return (
    <FlexContainer justify="flex-end">
      <div css={{ minHeight: "24px", minWidth: "11rem" }}>
        {editMode ? (
          <DatePickerField
            value={value}
            setValue={updateValue}
            close={() => setEditMode(false)}
          />
        ) : (
          <Text tag="span">
            {value ? dayjs(value).format(dateFormat) : "-"}
          </Text>
        )}
      </div>

      {canEdit && <EditButton onClick={() => setEditMode(true)} />}

      {canEdit && value && !mandatoryDate && (
        <InlineActionButton
          type="button"
          css={{ marginLeft: "0" }}
          icon="trash"
          size="sm"
          severity="low"
          onClick={() => clearDate()}
        />
      )}
    </FlexContainer>
  );
}

export { DatePicker };
